import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { fetchBrushes } from "../lib/api";
import SEO from "../components/SEO";
import StructuredData, { breadcrumbSchema } from "../components/StructuredData";

const CATS = [
  { id: "all", label: "All" },
  { id: "face", label: "Face" },
  { id: "cheeks", label: "Cheeks" },
  { id: "eyes", label: "Eyes" },
  { id: "lips", label: "Lips" },
];

export default function Catalog() {
  const location = useLocation();
  const [activeCat, setActiveCat] = useState("all");
  const [brushes, setBrushes] = useState([]);
  const [loading, setLoading] = useState(true);

  // Read ?category=face param
  useEffect(() => {
    const p = new URLSearchParams(location.search).get("category");
    if (p && CATS.find((c) => c.id === p)) setActiveCat(p);
  }, [location.search]);

  useEffect(() => {
    setLoading(true);
    fetchBrushes(activeCat)
      .then((d) => setBrushes(d.brushes))
      .finally(() => setLoading(false));
  }, [activeCat]);

  const groupedCount = useMemo(() => brushes.length, [brushes]);

  return (
    <div data-testid="catalog-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
      <SEO
        title="The Brush Catalog — All 30, Filterable by Category"
        description="Browse the full BrushBasics catalog of 30 curated makeup brushes. Filter by face, cheeks, eyes, or lips. Each with what it's for and how to use it."
        path="/catalog"
      />
      <StructuredData
        schemas={[
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Catalog", path: "/catalog" },
          ]),
        ]}
      />
      <header className="mb-12 lg:mb-16">
        <div className="overline mb-4">The Catalog</div>
        <h1 className="font-serif-display text-5xl sm:text-6xl font-light text-stone-900 tracking-tight">
          The complete brush index.
        </h1>
        <p className="mt-5 max-w-2xl text-base text-stone-600 leading-relaxed">
          Every brush deserves a moment of focus. Filter by where it belongs on the face, then click any brush for a
          full technique breakdown.
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-2 mb-12 border-b border-[#E5E0D8] pb-4" data-testid="catalog-filters">
        {CATS.map((c) => (
          <button
            key={c.id}
            data-testid={`filter-${c.id}`}
            onClick={() => setActiveCat(c.id)}
            className={`text-xs uppercase tracking-[0.22em] px-4 py-2 transition-colors ${
              activeCat === c.id
                ? "bg-[#2C2A29] text-white"
                : "text-stone-500 hover:text-stone-900"
            }`}
          >
            {c.label}
          </button>
        ))}
        <Link
          to="/brush/powder-brush"
          data-testid="catalog-free-sample-chip"
          className="ml-auto inline-flex items-center gap-2 bg-[#E8D5D1] hover:bg-[#dec4be] text-stone-900 px-3 py-2 text-xs transition-colors"
        >
          <span className="overline" style={{ color: "#2C2A29" }}>Free Sample</span>
          <span>Try a tutorial →</span>
        </Link>
        <span className="text-xs text-stone-500" data-testid="catalog-count">
          {loading ? "Loading…" : `${groupedCount} brushes`}
        </span>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-[#F2EFEB] h-[460px] animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12" data-testid="catalog-grid">
          {brushes.map((b, i) => (
            <Link
              key={b.id}
              to={`/brush/${b.id}`}
              data-testid={`brush-card-${b.id}`}
              className="group block bg-white border border-[#E5E0D8] bb-card-hover"
            >
              <div className="aspect-[4/5] overflow-hidden bg-[#F2EFEB]">
                <img
                  src={b.image}
                  alt={b.name}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700"
                />
              </div>
              <div className="p-6">
                <div className="overline mb-3">{b.category}</div>
                <h3 className="font-serif-display text-2xl text-stone-900 leading-snug">{b.name}</h3>
                <p className="mt-2 text-sm text-stone-500 italic">{b.tagline}</p>
                <div className="mt-5 flex items-center justify-between">
                  <span className="text-xs text-stone-500">{b.skill}</span>
                  <span className="text-xs uppercase tracking-[0.22em] text-[#8C6A5D] group-hover:text-[#70544A]">
                    Read →
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
